import {
    Component,
    ComponentInterface,
    Element,
    forceUpdate,
    Prop,
    h,
    Host,
    State,
    Watch,
} from "@stencil/core";
import { i18nProvider } from "@trumpf/ux.i18n";

import {
    ContentDataProvider,
    ContentDataProviderState,
    ContentDataSchema,
    getContentDataProvider,
    FilterDataProvider,
    getFilterDataProvider,
} from "../../../apis/stateful-content";
import {
    getTranslateFn,
    TranslateFn,
    getTranslationSource,
    TranslationRenderType,
    detectLoadMorePage,
    detectLoadMoreModal,
} from "../stateful-content-utils";
import { initStatefulOverviewTracking } from "./tracking";

@Component({
    tag: "ux-iws-stateful-overview",
    styleUrl: "stateful-overview.scss",
})
export class StatefulOverview implements ComponentInterface {
    @Element() domNode: HTMLUxIwsStatefulOverviewElement;
    private baseClass = "ux-iws-stateful-overview";
    private filterProvider: FilterDataProvider;
    private dataProvider: ContentDataProvider;
    private scrollSensor: HTMLUxInfiniteScrollSensorElement;
    private translate: TranslateFn;
    private translationRenderType: TranslationRenderType =
        TranslationRenderType.Content;

    /** endpoint where filter data can be requested */
    @Prop() url: string = "";
    /** print debug info */
    @Prop() debug: boolean = false;
    /** scope used to retrieve the content data provider */
    @Prop() scope: string = "";
    /** show a switch between table and tile view */
    @Prop() switch: boolean = false;
    /** label for the count result text */
    @Prop() countResultLabel: string = "";
    /** maximum number of pixels that the fixed column in the stateful-table is allowed to be wide */
    @Prop() maxFixedColWidth: number;
    /** toggle for showing filtered and total count */
    @Prop() showCount: boolean = true;
    /** toggle to define the initial view */
    @Prop() view: "table" | "tiles" = "table";

    @State() startDate = "";
    @State() endDate = "";
    @State() sorting = {};
    @State() isDataLoaded: boolean = false;
    @State() isFiltered: boolean = false;
    @State() checkItemsCallCountPage: number = 0;
    @State() checkItemsCallCountModal: number = 0;

    private i18nSubscriberId: string;

    connectedCallback() {
        this.i18nSubscriberId = i18nProvider.subscribe(() => forceUpdate(this));
    }

    disconnectedCallback() {
        i18nProvider.unsubscribe(this.i18nSubscriberId);
    }

    componentWillLoad() {
        this.dataProvider = getContentDataProvider(this.scope);
        this.dataProvider.subscribe(this.handleDataProviderState);
        this.filterProvider = getFilterDataProvider(this.scope);
        this.dataProvider.setEndpoint(this.url);
        this.setTranslations();
    }

    /**
     * handle script tag translations as well as deprecated translations prop fallback
     **/
    private setTranslations = () => {
        this.translate = getTranslateFn(
            getTranslationSource(
                this.scope,
                this.domNode,
                this.translationRenderType
            )
        );
    };

    componentDidLoad(): void {
        this.scrollSensor.addEventListener(
            "uxInfiniteScrollSensorScrolledIntoView",
            () => {
                this.dataProvider.loadMore();
            }
        );
        initStatefulOverviewTracking(this.scope, this.debug);
    }

    @Watch("view")
    refreshDataOnViewChange() {
        this.dataProvider.loadFirstPage();
    }

    private handleDataProviderState = ({ data }: ContentDataProviderState) => {
        this.isDataLoaded = true;
        this.isFiltered = this.filterProvider.state.filterQuerySet;
        if (data?.totalCount === 0 && !this.isFiltered) {
            return this.handleEmptyResults();
        }
        if (data !== null && data.schema !== undefined) {
            this.sorting = this.getSortingLabels(
                this.getSortableFields(data.schema),
                data.schema
            );
            this.scrollSensor.hasFired = false;
            detectLoadMorePage(this.dataProvider);
            detectLoadMoreModal(this.dataProvider);
        } else {
            this.sorting = {};
        }
    };

    private handleEmptyResults = () => {
        document
            .querySelector(
                `ux-iws-stateful-filter-button[scope="${this.scope}"]`
            )
            ?.parentElement.remove();
        document
            .querySelector(`ux-iws-stateful-filter[scope="${this.scope}]`)
            ?.remove();
        document
            .querySelector(
                `ux-iws-stateful-filter-search[scope="${this.scope}"]`
            )
            ?.parentElement?.remove();
        this.scrollSensor.hasFired = true;
    };

    private handleSelectSorting = (event: CustomEvent<string>) => {
        this.dataProvider.setSortParam(event.detail);
    };

    private getSortableFields(schema: ContentDataSchema) {
        return Object.keys(schema).filter((key) => schema[key].sortable);
    }

    private getSortingLabels(
        sortableFields: string[],
        schema: ContentDataSchema
    ) {
        return sortableFields.reduce(
            (labels, field) => ({
                ...labels,
                [field]:
                    schema[field].labels?.column ||
                    this.translate(`${field}.column`),
            }),
            {}
        );
    }

    private getErrorHeadline = (): string => {
        const { head } = this.dataProvider.error || {};
        if (typeof head === "string" && head !== "") {
            return head;
        }
        const key = this.isFiltered ? "filterError.headline" : "error.headline";
        const text = this.translate(key);
        return text !== key ? text : "";
    };

    private getErrorText = (): string => {
        const { copy, message } = this.dataProvider.error || {};
        const textFromApi = copy || message;
        if (typeof textFromApi === "string" && textFromApi !== "") {
            return textFromApi;
        }
        const key = this.isFiltered ? "filterError.text" : "error.text";
        const text = this.translate(key);
        return text !== key ? text : "";
    };

    render() {
        const { t } = i18nProvider;
        const { data, pending } = this.dataProvider;
        const {
            items,
            schema,
            filteredCount = 0,
            displayedCount = 0,
            totalCount = 0,
            labelCount,
        } = data || {};
        const hasData = Array.isArray(items) && items.length > 0;

        const errorHeadline = this.getErrorHeadline();
        const errorText = this.getErrorText();

        const countLabel =
            labelCount || this.countResultLabel || t("results.counter.label");
        const sortingOptions = Object.keys(this.sorting).map((key) => [
            <option key={key + "-asc"} value={key + "-asc"}>
                {this.sorting[key]} {t("sorting.ascending")}
            </option>,
            <option key={key + "-desc"} value={key + "-desc"}>
                {this.sorting[key]} {t("sorting.descending")}
            </option>,
        ]);

        return (
            <Host class={this.baseClass}>
                <ux-spinner-overlay {...(pending ? { opened: true } : {})} />
                {hasData && (
                    <div class="ux-row">
                        <div class="ux-col-10 ux-u-offset-1 ux-u-mt-xxs results">
                            {this.showCount && (
                                <div class="results__numbers ux-text-headline ux-text-headline--small">
                                    <span id="content-filtered-number">
                                        {displayedCount || filteredCount}
                                    </span>
                                    /
                                    <span id="content-total-number">
                                        {totalCount}
                                    </span>
                                    <span
                                        class={`${this.baseClass}__label-count`}
                                    >
                                        {countLabel}
                                    </span>
                                </div>
                            )}
                            <div class="results__sorting">
                                {this.switch && (
                                    <div class="ux-button-group">
                                        <ux-button
                                            size="icon"
                                            icon="list"
                                            selected={this.view === "table"}
                                            onClick={() =>
                                                (this.view = "table")
                                            }
                                        />
                                        <ux-button
                                            size="icon"
                                            icon="tiles"
                                            selected={this.view === "tiles"}
                                            onClick={() =>
                                                (this.view = "tiles")
                                            }
                                        />
                                    </div>
                                )}
                                {this.view === "tiles" && (
                                    <ux-select
                                        name="select"
                                        class="results__sorting-select"
                                        onUxSelectChange={
                                            this.handleSelectSorting
                                        }
                                    >
                                        {[...sortingOptions]}
                                    </ux-select>
                                )}
                            </div>
                        </div>
                        {this.view === "tiles" && (
                            <div class="ux-col-10 ux-u-offset-1 ux-u-mt-xxs results__sorting-select--mobile">
                                <ux-select
                                    name="select"
                                    onUxSelectChange={this.handleSelectSorting}
                                    full-width
                                >
                                    {[...sortingOptions]}
                                </ux-select>
                            </div>
                        )}
                    </div>
                )}
                {this.view === "table" &&
                    totalCount > 0 &&
                    schema !== undefined && (
                        <div class="ux-row">
                            <div class="ux-col-12 ux-col-md-10 ux-u-offset-md-1 ux-u-mt-xxs ">
                                <ux-iws-stateful-table
                                    scope={this.scope}
                                    max-fixed-col-width={this.maxFixedColWidth}
                                />
                            </div>
                        </div>
                    )}
                {!hasData && !pending && this.isDataLoaded && (
                    <div class="empty-results">
                        <div class="ux-row">
                            <div class="ux-col-10 ux-u-offset-1">
                                <div
                                    class={`ux-u-pt-fluid-m u-background-white ux-u-pb-m ${
                                        totalCount > 0 ? "" : "ux-u-mt-fluid-m"
                                    }`}
                                >
                                    {totalCount === 0 && (
                                        <ux-icon name="cross" weight="normal" />
                                    )}
                                    {errorHeadline && (
                                        <div class="ux-text-headline ux-text-headline--medium ux-u-mt-xs">
                                            {errorHeadline}
                                        </div>
                                    )}
                                    {errorText && (
                                        <p class="ux-text-normal ux-text-normal--light-grey ux-u-mt-xxxs">
                                            {errorText}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                {this.view === "tiles" && totalCount > 0 && (
                    <div class="ux-row">
                        <div class="ux-col-12 ux-col-md-10 ux-u-offset-md-1 ux-u-mt-xxs">
                            <ux-iws-stateful-tiles scope={this.scope} />
                        </div>
                    </div>
                )}
                <ux-infinite-scroll-sensor
                    ref={(el: HTMLUxInfiniteScrollSensorElement) =>
                        (this.scrollSensor = el)
                    }
                />
                <slot name="translations" />
            </Host>
        );
    }
}
